import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import config from "../../config/config";
import { FONT_FAMILY } from "../../config/typography";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  Stack,
  ThemeProvider,
  createTheme,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Print as PrintIcon,
  VerifiedUser as VerifiedIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Image as ImageIcon
} from "@mui/icons-material";

// Sade ve Kurumsal Tema
const theme = createTheme({
  palette: {
    primary: {
      main: "#2C3E50",
    },
    secondary: {
      main: "#34495E",
    },
    background: {
      default: "#F8F9FA",
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
  },
});

function CertificaPage() {
  const { uid } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [certificateImages, setCertificateImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  // Tüm sertifikaları görsellere çevir ve aç
  const handleGenerateAllImages = async () => {
    setGeneratingImages(true);
    try {
      const response = await fetch(`${config.baseURL}/certificate/generate/${uid}`);
      const data = await response.json();

      if (data.success && data.certificates) {
        // Yeni sekmede görselleri göster
        const newWindow = window.open('', '_blank');
        newWindow.document.write('<html><head><title>Sertifikalar</title></head><body style="margin:0;padding:20px;background:#f5f5f5;">');

        data.certificates.forEach((cert, index) => {
          newWindow.document.write(`
            <div style="margin-bottom:20px;background:white;padding:10px;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1);">
              <h3 style="margin:0 0 10px 0;">Sertifika ${index + 1} - ${cert.dateRange}</h3>
              <img src="${cert.data}" style="width:100%;height:auto;display:block;" />
            </div>
          `);
        });

        newWindow.document.write('</body></html>');
        newWindow.document.close();
      }
    } catch (error) {
      console.error("Sertifika görselleri oluşturulamadı:", error);
      alert("Sertifikalar oluşturulurken bir hata oluştu.");
    } finally {
      setGeneratingImages(false);
    }
  };

  // ZIP olarak indir
  const handleDownloadZip = async () => {
    setDownloadingZip(true);
    try {
      const response = await fetch(`${config.baseURL}/certificate/download/${uid}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Sertifikalar-${certificateData.licenseNo}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert("ZIP dosyası indirilemedi.");
      }
    } catch (error) {
      console.error("ZIP indirme hatası:", error);
      alert("İndirme sırasında bir hata oluştu.");
    } finally {
      setDownloadingZip(false);
    }
  };

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await fetch(`${config.baseURL}/certificate/${uid}`);
        const data = await res.json();
        setCertificateData(data);

        // Sertifika görsellerini backend'den al
        const imageRes = await fetch(`${config.baseURL}/certificate/generate/${uid}`);
        const imageData = await imageRes.json();

        if (imageData.success && imageData.certificates) {
          setCertificateImages(imageData.certificates);
        }
      } catch (err) {
        console.error("Sertifika yüklenemedi:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [uid]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            height: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default"
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={50} sx={{ color: "primary.main", mb: 2 }} />
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Sertifika yükleniyor...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (!certificateData || !certificateData.licenses || certificateData.licenses.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            height: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default"
          }}
        >
          <Card
            sx={{
              maxWidth: 450,
              textAlign: "center",
              p: 5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
            }}
          >
            <ErrorIcon sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="600">
              Sertifika Bulunamadı
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aradığınız sertifika sistemde kayıtlı değil.
            </Typography>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  const { company, packageName, licenses, licenseNo } = certificateData;

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="certificate-page"
        sx={{
          height: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            maxWidth: "lg",
            width: "100%",
            mx: "auto",
            px: 2
          }}
        >
          {/* Sabit Header */}
          <Box className="no-print" sx={{ textAlign: "center", py: 2, flexShrink: 0 }}>
            <VerifiedIcon sx={{ fontSize: 40, color: "primary.main", mb: 0.5 }} />
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: "600", mb: 0.5 }}
            >
              Lisans Sertifikası
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dijital sertifika bilgilerinizi görüntüleyin
            </Typography>
          </Box>

          {/* Sabit Butonlar */}
          <Stack
            direction="row"
            justifyContent="center"
            spacing={1}
            className="no-print"
            sx={{ mb: 2, flexShrink: 0 }}
          >
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                px: 4,
                py: 1.2,
                textTransform: "none",
                fontWeight: "500",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "primary.dark",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                }
              }}
            >
              Yazdır / PDF Kaydet
            </Button>

            <Tooltip title="Tüm sertifikaları görsellere çevir ve görüntüle">
              <IconButton
                onClick={handleGenerateAllImages}
                disabled={generatingImages}
                sx={{
                  bgcolor: "secondary.main",
                  color: "white",
                  "&:hover": { bgcolor: "secondary.dark" },
                  width: 45,
                  height: 45
                }}
              >
                {generatingImages ? <CircularProgress size={24} color="inherit" /> : <ImageIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Tüm sertifikaları ZIP olarak indir">
              <IconButton
                onClick={handleDownloadZip}
                disabled={downloadingZip}
                sx={{
                  bgcolor: "success.main",
                  color: "white",
                  "&:hover": { bgcolor: "success.dark" },
                  width: 45,
                  height: 45
                }}
              >
                {downloadingZip ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Sertifika Kartları Scroll Kutusu (Bilgi Kartı İçinde) */}
          <Box
            className="no-print"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              bgcolor: "white",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#555",
                },
              },
            }}
          >
            {/* Sertifika Bilgi Kartı - Scroll İçinde */}
            <Card
              sx={{
                mb: 2,
                mx: 2,
                mt: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 1,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="600" color="primary.main">
                  Sertifika Bilgileri
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
                    >
                      Firma Adı
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {company}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
                    >
                      Paket Adı
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {packageName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
                    >
                      Lisans No
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {licenseNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
                    >
                      Toplam Lisans
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {licenses.length} Adet
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sertifika Kartları */}
            <Box sx={{ px: 2, pb: 2 }}>
              {certificateImages.map((cert, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    borderRadius: 1,
                    overflow: "hidden"
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "primary.main",
                      px: 3,
                      py: 1.5
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "white", fontWeight: "500" }}
                    >
                      Lisans #{index + 1} - {cert.dateRange}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "29.7 / 21",
                      position: "relative",
                      overflow: "hidden"
                    }}
                  >
                    <img
                      src={cert.data}
                      alt={`Sertifika ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block"
                      }}
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Yazdırma İçin Gizli Versiyon */}
          <Box className="print-only" sx={{ display: "none" }}>
            {certificateImages.map((cert, index) => (
              <Box
                key={index}
                className={`certificate-print-page ${index > 0 ? 'certificate-page-break' : ''}`}
                sx={{
                  width: "29.7cm",
                  height: "21cm",
                  position: "relative",
                  pageBreakAfter: "always",
                  pageBreakInside: "avoid",
                  breakAfter: "page",
                  breakInside: "avoid",
                  overflow: "hidden"
                }}
              >
                <img
                  src={cert.data}
                  alt={`Sertifika ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block"
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <Box
            className="no-print"
            sx={{
              py: 1.5,
              textAlign: "center",
              borderTop: "1px solid #e0e0e0",
              flexShrink: 0,
              bgcolor: "background.default"
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Bu sertifika <strong>Pozitif Ofis</strong> tarafından dijital olarak oluşturulmuştur.
            </Typography>
          </Box>
        </Box>
      </Box>

      <style>{`
        #root {
          display: block;
        }

        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 29.70cm !important;
            height: auto !important;
            overflow: visible !important;
          }

          .no-print, .no-print * {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }


          .certificate-page, .certificate-container {
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            height: auto !important;
            width: 29.7cm !important;
            overflow: visible !important;
          }

          .certificate-page .css-tpfsku{
            padding: 0 !important;
          }

          .print-only {
            display: block !important;
            visibility: visible !important;
            width: 29.7cm !important;
            height: auto !important;
            overflow: visible !important;
            position: relative !important;
          }

          .certificate-print-page {
            page-break-after: always !important;
            page-break-before: auto !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            break-before: auto !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 29.7cm !important;
            height: 21cm !important;
            min-height: 21cm !important;
            max-height: 21cm !important;
            box-sizing: border-box !important;
            display: block !important;
            position: relative !important;
            overflow: hidden !important;
          }

          .certificate-print-page img {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain !important;
            display: block !important;
          }

          .certificate-page-break {
            page-break-before: always !important;
            break-before: page !important;
          }

          .certificate-print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }

        @page {
          size: A4 landscape;
          margin: 0;
        }
      `}</style>
    </ThemeProvider>
  );
}

export default CertificaPage;